'use client'

import React from 'react'
import {useTranslations} from 'next-intl'
import {css, useTheme} from '@emotion/react'
import {Typography} from '@mui/material'

import {ProcessStatus} from '../../../../constants'
import {useAppCtx} from '../../../../contexts/appCtx'
import ProcessStatusInfo from '../../../../components/modules/ProcessStatusInfo'
import TextField from '../../../../components/elements/TextField'
import Paper from '../../../../components/elements/Paper'
import generateRandomColorCode from './generateRandomColorCode'
import Button from '../../../../components/elements/Button'
import InputResultPanelLayout from '../../../../components/layouts/InputResultPanelLayout'
import convertColorCode from './convertColorCode'
import ColorTable from './ColorTable'
import dynamic from 'next/dynamic'


const ColorDialog = dynamic(() => import('../../../../components/ColorDialog'), {
  ssr: false,
})

const DefaultColor = '#3949ab'

export default function ColorConverter() {
  const theme = useTheme()
  const styles = {
    // panel
    content: css`
      background: ${theme.palette.surface.main};

      padding: 16px;

      @media (max-width: ${theme.breakpoints.sm}px) {
        padding: 16px;
      }
    `,

    description: css`
      margin-bottom: 8px;
    `,

    textInput: css`
      height: 40px;
      max-width: 400px;
    `,

    chooseButton: css`
      text-transform: uppercase;
      font-weight: bold;
    `,

    randomButton: css`
      background-image: linear-gradient(90deg, #00c0ff 0%, #FFCF00 49%, #FC4F4F 80%, #00C0FF 100%);
      text-transform: uppercase;
      font-weight: bold;

      &:hover {
        animation: slideColor 5s linear infinite;
      }

      @keyframes slideColor {
        to {
          background-position: 20vw;
        }
      }
    `,

    hint: css`
      margin-top: 8px;
      font-size: 0.9em;
    `,

    processStatus: css`
      position: absolute;
      top: 8px;
      right: 8px;
    `,

    colorPanel: css`
      margin-top: 16px;
      height: 150px;

      box-sizing: border-box;
      box-shadow: rgba(0, 0, 0, 0.12) 0 1px 6px, rgba(0, 0, 0, 0.12) 1px 1px 1px;
      border-radius: 3px;

      display: flex;
      align-items: center;
      justify-content: center;
    `,
    colorBlock: css`
      width: 300px;
      height: 100px;

      box-sizing: border-box;
      box-shadow: rgba(0, 0, 0, 0.12) 0 1px 6px, rgba(0, 0, 0, 0.12) 1px 1px 1px;
      border-radius: 3px;

      display: flex;
      align-items: center;
      justify-content: center;

      font-size: 1.3rem;
    `,

    colorTable: css`
      margin-top: 16px;
    `,
  }

  const appCtx = useAppCtx()
  const t = useTranslations()

  const [processStatus, setProcessStatus] = React.useState(ProcessStatus.Idle)
  const [colorInfo, setColorInfo] = React.useState(null)

  const [textInput, setTextInput] = React.useState(DefaultColor)
  const onTextInputChange = (textInput) => {
    setTextInput(textInput)
  }

  const [colorDialogOpen, setColorDialogOpen] = React.useState(false)
  const onColorDialogOpen = () => {
    setColorDialogOpen(true)
  }
  const onColorDialogClose = () => {
    setColorDialogOpen(false)
  }

  const onColorChange = (color) => {
    setTextInput(color)
  }

  React.useEffect(() => {
    setProcessStatus(ProcessStatus.Processing)
    try {
      const colorInfo = convertColorCode(textInput)
      setColorInfo(colorInfo)
      setProcessStatus(ProcessStatus.Success)
    } catch (e) {
      console.log(e)
      setColorInfo(null)
      setProcessStatus(ProcessStatus.Failed)
    }
  }, [textInput])

  const onRandomButtonClick = () => {
    setTextInput(generateRandomColorCode())
  }

  return (
    <>
      <InputResultPanelLayout
        inputContent={(
          <Paper css={styles.content}>
            <div>
              <Typography css={styles.description}>{t('color-converter.input-box-description')}</Typography>

              <div css={css`
                display: flex;
                justify-content: space-between;
              `}>
                <div css={css`
                  flex: 1;
                  display: flex;
                  gap: 8px;
                `}>
                  <Button css={styles.chooseButton}
                          onClick={onColorDialogOpen}>{t('color-converter.button-choose')}</Button>

                  <TextField
                    css={styles.textInput}
                    placeholder={t('color-converter.input-placeholder')}
                    value={textInput}
                    onChange={onTextInputChange}
                  />
                </div>
                <div css={css`flex: 1`}></div>
                <Button css={styles.randomButton}
                        onClick={onRandomButtonClick}>{t('color-converter.button-random')}</Button>
              </div>

              <Typography
                css={styles.hint}
                dangerouslySetInnerHTML={
                  {__html: t('color-converter.input-hint', {interpolation: {escapeValue: false}})}
                }/>
            </div>
          </Paper>
        )}
        inputBottom={(
          appCtx.isMobile ? (
            <div css={css`
              width: 100%;
              display: flex;
              align-items: center;
              justify-content: space-between;
            `}>
              <ProcessStatusInfo id="process-status" processStatus={processStatus}/>
            </div>
          ) : (
            <div/>
          )
        )}

        resultContent={(
          <Paper css={styles.content}>
            <ProcessStatusInfo css={styles.processStatus} id="process-status" processStatus={processStatus}/>

            <div css={{display: 'flex', alignItems: 'center'}}>
              <Typography>{t('color-converter.assume-description')}</Typography>
              <TextField
                css={css`
                  height: 40px;
                  margin-left: 16px;
                  width: fit-content;
                `}
                readOnly={true}
                value={colorInfo?.model?.toUpperCase()}
              />
            </div>
            <div css={styles.colorPanel}>
              <div
                css={styles.colorBlock}
                style={{
                  background: colorInfo?.rgb,
                  color: colorInfo?.isDark ? 'white' : 'black'
                }}
              >{colorInfo?.hex}</div>
            </div>
            <ColorTable css={styles.colorTable} rows={[
              {
                label: t('color-converter.title-name'),
                value: colorInfo?.rgb ? (colorInfo?.name || t('color-converter.no-name')) : ''
              },
              {label: t('color-converter.title-rgb'), value: colorInfo?.rgb},
              {label: t('color-converter.title-hex'), value: colorInfo?.hex},
              {label: t('color-converter.title-hsl'), value: colorInfo?.hsl},
              {label: t('color-converter.title-cmyk'), value: colorInfo?.cmyk},
              {label: t('color-converter.title-hwb'), value: colorInfo?.hwb},
            ]}/>
          </Paper>
        )}
      />

      <ColorDialog
        open={colorDialogOpen}
        onClose={onColorDialogClose}
        onConfirm={onColorChange}
        initColor={textInput}
      />
    </>
  )
}
